/**
 * MergeRequestsPage — Alfred review queue for parent-initiated family merges.
 *
 * Parents in the member app can request that a duplicate family be folded into
 * theirs, but only after OTP-verifying the source phone. Those requests land
 * here as PENDING; an admin reviews what would move and either APPROVES (which
 * runs the real, irreversible ParentMergeService.merge on the backend) or
 * REJECTS with a reason.
 *
 * Self-contained — no Redux integration. Calls the admin merge-request client
 * directly (see src/api/parent_guardian.js).
 */
import {
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField
} from '@material-ui/core'
import dayjs from 'dayjs'
import React from 'react'

import { approveMergeRequest, listMergeRequests, rejectMergeRequest } from 'api'
import {
  Card,
  CardBody,
  CardHeader,
  CardTitle
} from 'app/components/generic/Card'
import constants from 'app/constants'
import { eventAlert, showToast } from 'app/helpers'

const STATUS_FILTERS = [
  { value: 'pending', label: 'Pending' },
  { value: 'all', label: 'All' }
]

const STATUS_COLORS = {
  pending: { color: '#8a6d00', background: '#fff4cc' },
  approved: { color: '#1b5e20', background: '#e0f2e1' },
  rejected: { color: '#b71c1c', background: '#fde0e0' },
  cancelled: { color: '#555', background: '#eee' }
}

const StatusChip = ({ status }) => {
  const style = STATUS_COLORS[status] || STATUS_COLORS.cancelled
  return (
    <Chip
      size="small"
      label={status}
      style={{ ...style, fontWeight: 600, textTransform: 'capitalize' }}
    />
  )
}

const MergeRequestsPage = () => {
  const [requests, setRequests] = React.useState([])
  const [loading, setLoading] = React.useState(true)
  const [statusFilter, setStatusFilter] = React.useState('pending')
  const [actioningUuid, setActioningUuid] = React.useState(null)
  const [rejectFor, setRejectFor] = React.useState(null)
  const [rejectReason, setRejectReason] = React.useState('')

  const refresh = React.useCallback(async () => {
    setLoading(true)
    try {
      const data = await listMergeRequests(statusFilter)
      setRequests(Array.isArray(data) ? data : [])
    } catch (error) {
      eventAlert(
        error?.response?.data?.message ||
          error?.message ||
          'Failed to load merge requests',
        constants.ERROR
      )
    } finally {
      setLoading(false)
    }
  }, [statusFilter])

  React.useEffect(() => {
    refresh()
  }, [refresh])

  const handleApprove = async (request) => {
    // eslint-disable-next-line no-alert
    if (
      !window.confirm(
        `Approve merge of ${
          request.source?.name || request.source_phone
        } into ` +
          `${request.target?.name || 'this family'}?\n\n` +
          'This runs the merge immediately and CANNOT be undone — the source ' +
          'account will be closed.'
      )
    ) {
      return
    }
    setActioningUuid(request.uuid)
    try {
      await approveMergeRequest(request.uuid)
      showToast('Merge approved and executed', constants.SUCCESS)
      await refresh()
    } catch (error) {
      eventAlert(
        error?.response?.data?.message ||
          error?.response?.data?.detail ||
          error?.message ||
          'Failed to approve merge',
        constants.ERROR
      )
    } finally {
      setActioningUuid(null)
    }
  }

  const handleReject = async () => {
    if (!rejectFor) return
    setActioningUuid(rejectFor.uuid)
    try {
      await rejectMergeRequest(rejectFor.uuid, rejectReason)
      showToast('Merge request rejected', constants.SUCCESS)
      setRejectFor(null)
      setRejectReason('')
      await refresh()
    } catch (error) {
      eventAlert(
        error?.response?.data?.message ||
          error?.message ||
          'Failed to reject merge request',
        constants.ERROR
      )
    } finally {
      setActioningUuid(null)
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Guardian Requests</CardTitle>
        <div style={{ display: 'flex', gap: 8 }}>
          {STATUS_FILTERS.map((f) => (
            <Button
              key={f.value}
              size="small"
              variant={statusFilter === f.value ? 'contained' : 'outlined'}
              color="primary"
              onClick={() => setStatusFilter(f.value)}
            >
              {f.label}
            </Button>
          ))}
        </div>
      </CardHeader>
      <CardBody>
        <div style={{ color: '#888', fontSize: 13, marginBottom: 12 }}>
          Parent-initiated merges await your approval. Approving runs the merge
          immediately and cannot be undone.
        </div>

        {loading ? (
          <CircularProgress size={22} />
        ) : requests.length === 0 ? (
          <div style={{ color: '#888', fontSize: 14 }}>
            No {statusFilter === 'all' ? '' : statusFilter} merge requests.
          </div>
        ) : (
          requests.map((request) => (
            <MergeRequestRow
              key={request.uuid}
              request={request}
              busy={actioningUuid === request.uuid}
              onApprove={() => handleApprove(request)}
              onReject={() => {
                setRejectReason('')
                setRejectFor(request)
              }}
            />
          ))
        )}
      </CardBody>

      <Dialog
        open={!!rejectFor}
        onClose={() => actioningUuid === null && setRejectFor(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Reject merge request?</DialogTitle>
        <DialogContent>
          <div style={{ fontSize: 14, marginBottom: 12 }}>
            Rejecting closes this request without merging. The parent can
            request again later.
          </div>
          <TextField
            label="Reason (optional)"
            fullWidth
            multiline
            minRows={2}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setRejectFor(null)}
            disabled={actioningUuid !== null}
          >
            Cancel
          </Button>
          <Button
            onClick={handleReject}
            color="secondary"
            variant="contained"
            disabled={actioningUuid !== null}
          >
            {actioningUuid ? 'Rejecting…' : 'Reject request'}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  )
}

const MergeRequestRow = ({ request, busy, onApprove, onReject }) => {
  const counts = request.preview_snapshot?.counts || {}
  const isPending = request.status === 'pending'
  return (
    <div
      style={{
        border: '1px solid #eee',
        borderRadius: 8,
        padding: 16,
        marginBottom: 12
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 8
        }}
      >
        <div style={{ fontSize: 15, fontWeight: 600 }}>
          {request.source?.name || 'Unknown'} ({request.source_phone}){'  →  '}
          {request.target?.name || 'Target family'}
        </div>
        <StatusChip status={request.status} />
      </div>

      <div style={{ fontSize: 13, color: '#555', marginBottom: 8 }}>
        Requested by {request.requested_by_name || '—'}
        {request.created
          ? ` · ${dayjs(request.created).format('DD MMM YYYY, HH:mm')}`
          : ''}
        {request.source_phone_verified ? ' · source phone verified ✓' : ''}
      </div>

      <div style={{ fontSize: 13, color: '#444' }}>
        Would move: <b>{counts.students ?? 0}</b> student(s),{' '}
        <b>{counts.active_guardians ?? 0}</b> guardian(s),{' '}
        <b>{counts.coin_balance ?? 0}</b> coins,{' '}
        <b>{counts.subscriptions_total ?? 0}</b> subscription(s),{' '}
        <b>{counts.transactions ?? 0}</b> payment(s),{' '}
        <b>{counts.invoices ?? 0}</b> invoice(s)
      </div>

      {request.status === 'rejected' && request.rejection_reason ? (
        <div style={{ fontSize: 13, color: '#b71c1c', marginTop: 8 }}>
          Rejected: {request.rejection_reason}
        </div>
      ) : null}

      {isPending ? (
        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <Button
            size="small"
            variant="contained"
            color="primary"
            disabled={busy}
            onClick={onApprove}
            data-testid={`approve-${request.uuid}`}
          >
            {busy ? 'Working…' : 'Approve & merge'}
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="secondary"
            disabled={busy}
            onClick={onReject}
          >
            Reject
          </Button>
        </div>
      ) : null}
    </div>
  )
}

export default MergeRequestsPage
